/**
 * Системные тексты сервера на языке интерфейса (ADR-0019, agroai-rjk).
 *
 * Сервер отдаёт по-русски то, что не пользовательские данные, а системный
 * справочник: подписи метрик и единиц, этапы сезона, виды работ каталога и
 * работы демо-шаблонов, названия служебных кампаний, оговорки экранов,
 * названия детекторов сканера. Ответ API от языка не зависит (кэш ответов не
 * делится по языкам), поэтому перевод делает клиент: русская строка сервера —
 * это и запасной текст, и ключ поиска в каталоге пространства `server`.
 *
 *     srv(row.name)        // «Полив №1 (до цветения)» → «1-sugʻorish (gullashdan oldin)»
 *
 * Правила, на которых это держится:
 *
 * * промах безопасен: строки нет в словаре — возвращается сам текст сервера
 *   (русский), а не пусто и не id. Поэтому данные пользователей (названия
 *   хозяйств, его работы, комментарии) этой функцией не портятся: их в словаре
 *   нет, а совпадение с системным текстом дословно — это и есть тот же термин;
 * * `srv` — только для ПОКАЗА. Значение, которое экран потом отправит на
 *   сервер (поле формы, ключ группировки на запись), переводить нельзя;
 * * словарь — `serverVocab.ts`, его собирает `manage.py export_server_vocab`
 *   из `docs/model/*.yaml` и `docs/i18n/server-texts.yaml`;
 * * шаблонные строки несут `{имя}`: «Внеплановые поливы, сезон {season}» —
 *   значение вырезается регулярным выражением, а часть, которая сама есть в
 *   словаре, переводится тем же `srv`. Имя с суффиксом `Num` вырезает число,
 *   `Date` — дату ДД.ММ[.ГГГГ], `list…` — перечень частей (находки сканера).
 *   Фразы ответов ИИ (`ai_gateway/templates.yaml`) добавляют ещё три вида:
 *   `NumКонтур` — счётное слово («5 контуров»: вырезается число, слово
 *   согласуется по-русски, а в переводе стоит своё), `DateLong` — дата словами
 *   («27 марта 2026» → дата языка интерфейса), `Metric` — величина с единицей
 *   («85,0 %», «12 га»);
 * * составное «A: B» (кампания = «цикл: работа») переводится по частям.
 */
import type { Message } from './define';
import { dateLong } from '../astra/lib/format';
import { SERVER_NOUNS, SERVER_VOCAB } from './serverVocab';
import { isSourceLocale, runtimeLocale, tr } from './runtime';

const NBSP = '\u00a0';
/** Имя аргумента счётного слова: `s1NumContour` — число, за которым стоит «контур/контура/контуров». */
const NUM_NOUN = /Num([A-Z][A-Za-z]*)$/;
/** Русские месяцы в родительном падеже: так сервер печатает дату во фразе ответа. */
const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];
/** Единицы величины во фразе → единицы языка интерфейса (по `units.*` каталога). */
const METRIC_UNITS: Record<string, string> = { '%': '%', 'га': 'ga', 'мкга': 'mkga', 'дн.': 'kun' };

interface Entry {
  readonly message: Message;
  readonly args: readonly string[];
  readonly pattern: RegExp | null;
  /** Сколько в шаблоне литеральных знаков: чем больше, тем он конкретнее и тем раньше проверяется. */
  readonly weight: number;
}

/**
 * Что вырезает аргумент шаблона. По умолчанию — любой текст; по суффиксу имени
 * уже: `…Num` — число (со знаком, разрядами и запятой), `…Date` — дата
 * ДД.ММ[.ГГГГ]. Узкий класс нужен, чтобы «Карантин: красные контуры: 20 …» не
 * разрезалось по первому двоеточию. Для фраз ответов ИИ: `…NumContour` — число и
 * согласованное с ним слово (формы — `SERVER_NOUNS`), `…DateLong` — дата словами,
 * `…Metric` — величина с единицей.
 */
function argPattern(name: string): string {
  const noun = NUM_NOUN.exec(name)?.[1];
  if (noun) {
    // «5 контуров»: число вырезается, согласованное слово только опознаётся.
    const forms = [...(SERVER_NOUNS[noun] ?? [])].sort((a, b) => b.length - a.length);
    if (forms.length) return `(\\d[\\d\\s,.]*?)\\s(?:${forms.join('|')})`;
  }
  if (name.endsWith('Num')) return '([+−-]?\\d[\\d\\s ,.]*)';
  if (name.endsWith('DateLong')) return `(\\d{1,2}\\s(?:${MONTHS_GENITIVE.join('|')})\\s\\d{4})`;
  if (name.endsWith('Date')) return '(\\d{2}\\.\\d{2}(?:\\.\\d{4})?)';
  if (name.endsWith('Metric')) return '([+−-]?\\d[\\d\\s,.]*(?:\\s?(?:%|мкга|га|дн\\.))?)';
  return '(.+?)';
}

/** Значение аргумента на языке интерфейса; `null` — аргумент обычный, переводится как текст. */
function convertArg(name: string, part: string): string | null {
  if (NUM_NOUN.test(name) || name.endsWith('Num')) return part.trim().replace(/\s+/g, NBSP);
  if (name.endsWith('DateLong')) {
    const [day, month, year] = part.split(/\s+/);
    const index = MONTHS_GENITIVE.indexOf(month ?? '');
    if (index < 0) return null;
    // Месяцы — из каталога (`units.month.*`), как у остальных дат интерфейса: `Intl` для
    // узбекского есть не в каждом браузере и без данных печатает «2026 M03 27».
    return dateLong(`${year}-${String(index + 1).padStart(2, '0')}-${String(Number(day)).padStart(2, '0')}`);
  }
  if (name.endsWith('Metric')) {
    const unit = /\s?(%|мкга|га|дн\.)$/.exec(part);
    if (!unit) return part.replace(/\s+/g, NBSP);
    const number = part.slice(0, part.length - unit[0].length).trim().replace(/\s+/g, NBSP);
    return `${number}${NBSP}${METRIC_UNITS[unit[1]!] ?? unit[1]}`;
  }
  return null;
}

/** ICU-сообщение сервера → дословный текст, имена аргументов и (для шаблона) регулярное выражение. */
function compile(message: Message): { text: string; entry: Entry } {
  const src = message.defaultMessage;
  const args: string[] = [];
  let text = '';
  let pattern = '';
  let weight = 0;
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const literal = (ch: string) => {
    text += ch;
    pattern += escape(ch);
    weight += 1;
  };
  for (let i = 0; i < src.length; ) {
    const ch = src[i]!;
    if (ch === "'" && src[i + 1] === "'") {
      literal("'");
      i += 2;
    } else if (ch === "'" && /[{}<>]/.test(src[i + 1] ?? '') && src[i + 2] === "'") {
      literal(src[i + 1]!);
      i += 3;
    } else if (ch === '{') {
      const end = src.indexOf('}', i);
      const name = src.slice(i + 1, end);
      args.push(name);
      text += `{${name}}`;
      pattern += argPattern(name);
      i = end + 1;
    } else {
      literal(ch);
      i += 1;
    }
  }
  return { text, entry: { message, args, weight, pattern: args.length ? new RegExp(`^${pattern}$`) : null } };
}

let exact: Map<string, Entry> | null = null;
let lowered: Map<string, { text: string; entry: Entry }> = new Map();
let patterns: Entry[] = [];
const memo = new Map<string, string | null>();
let memoLocale = '';

function build(): Map<string, Entry> {
  if (exact) return exact;
  const map = new Map<string, Entry>();
  const list: Entry[] = [];
  lowered = new Map();
  for (const message of Object.values(SERVER_VOCAB) as Message[]) {
    const { text, entry } = compile(message);
    if (entry.pattern) list.push(entry);
    else {
      map.set(text, entry);
      lowered.set(text.toLowerCase(), { text, entry });
    }
  }
  list.sort((a, b) => b.weight - a.weight);
  patterns = list;
  exact = map;
  return map;
}

/** Показать текст сервера на языке интерфейса; нет в словаре — как есть. */
export function srv(text: string): string;
export function srv(text: string | null | undefined): string | null;
export function srv(text: string | null | undefined): string | null {
  if (text === null || text === undefined) return null;
  if (text === '' || isSourceLocale()) return text;
  return cached(text) ?? text;
}

/** Как `srv`, но промах — `null`: нужно, когда без перевода строку лучше не показывать. */
export function srvOrNull(text: string | null | undefined): string | null {
  if (text === null || text === undefined || text === '') return null;
  if (isSourceLocale()) return text;
  return cached(text);
}

/** Одна и та же строка идёт сотни раз (подпись работы в каждой строке списка): результат помнится. */
function cached(text: string): string | null {
  const locale = runtimeLocale();
  if (locale !== memoLocale) {
    memo.clear();
    memoLocale = locale;
  }
  if (memo.has(text)) return memo.get(text) ?? null;
  // Сервер разделяет разряды и число со словом неразрывным пробелом, а ключ словаря
  // записан обычным: для поиска пробелы приводятся к одному виду.
  const out = translate(text.replace(/\u00a0/g, ' '), 0);
  if (memo.size > 20000) memo.clear();
  memo.set(text, out);
  return out;
}

/** Перечень из находок сканера: части разделены «; » или «), » (скобка — часть элемента). */
function translateList(value: string, depth: number): string {
  const items: string[] = [];
  const seps: string[] = [];
  let rest = value;
  for (;;) {
    const semi = rest.indexOf('; ');
    const paren = rest.indexOf('), ');
    const afterParen = paren >= 0 && (semi < 0 || paren + 1 < semi);
    const at = afterParen ? paren + 1 : semi;
    if (at < 0) break;
    items.push(rest.slice(0, at));
    seps.push(rest.slice(at, at + 2));
    rest = rest.slice(at + 2);
  }
  items.push(rest);
  return items.map((item, i) => (translate(item, depth + 1) ?? item) + (seps[i] ?? '')).join('');
}

/** Длинные строки словаря (оговорки) от длинной к короткой: ими разбирается склейка. */
let longTexts: Array<[string, Entry]> | null = null;

/** «Оговорка1 Оговорка2 …» — каждая известная оговорка переводится, неизвестный хвост остаётся. */
function translateJoined(text: string): string | null {
  const map = build();
  longTexts ??= [...map.entries()].filter(([t]) => t.length > 40).sort((a, b) => b[0].length - a[0].length);
  const parts: string[] = [];
  let rest = text;
  let matched = 0;
  while (rest) {
    const hit = longTexts.find(([t]) => rest.startsWith(t) && (rest.length === t.length || rest[t.length] === ' '));
    if (!hit) {
      parts.push(rest);
      break;
    }
    parts.push(tr(hit[1].message as never));
    matched += 1;
    rest = rest.slice(hit[0].length + 1);
  }
  return matched > 0 ? parts.join(' ') : null;
}

function translate(text: string, depth: number): string | null {
  const map = build();
  const hit = map.get(text);
  if (hit) return tr(hit.message as never);
  if (depth > 4) return null;
  // Строчная форма подписи: сервер пишет `label.lower()` внутри предложений.
  if (text.length > 3) {
    const low = lowered.get(text.toLowerCase());
    if (low && low.text !== text) {
      const out = tr(low.entry.message as never);
      return text[0] === text[0]!.toLowerCase() ? out.charAt(0).toLowerCase() + out.slice(1) : out;
    }
  }
  for (const entry of patterns) {
    const match = entry.pattern!.exec(text);
    if (!match) continue;
    const values: Record<string, string> = {};
    entry.args.forEach((name, i) => {
      const part = match[i + 1]!;
      values[name] =
        convertArg(name, part) ??
        (name.startsWith('list') ? translateList(part, depth) : (translate(part, depth + 1) ?? part));
    });
    return tr(entry.message as never, values as never);
  }
  // Сервер склеивает несколько оговорок в одно поле через пробел (`water_note`).
  if (text.length > 80) {
    const joined = translateJoined(text);
    if (joined !== null) return joined;
  }
  // «Цикл: работа» — кампания плана; части переводятся каждая своим словарём.
  const colon = text.indexOf(': ');
  if (colon > 0) {
    const head = translate(text.slice(0, colon), depth + 1);
    const tail = translate(text.slice(colon + 2), depth + 1);
    if (head !== null || tail !== null) return `${head ?? text.slice(0, colon)}: ${tail ?? text.slice(colon + 2)}`;
  }
  return null;
}

/**
 * Пути, чей ответ показывается на языке интерфейса НЕ целиком:
 *
 * * `/ai/agent/…` — беседы и отчёты ассистента: текст пишет модель на языке
 *   вопроса, а сохранённый отчёт клиент отправляет обратно правкой;
 * * `/agrocycle/templates/<id>` и `/agrocycle/work-defs/…` — данные редактора
 *   шаблона: подписи этапов и работ там поле формы, и «показанный» перевод
 *   вернулся бы на сервер как правка менеджера. Чтение шаблона для показа
 *   (`…/live`, `…/changes`) переводится.
 */
const NOT_LOCALIZED = [/^\/ai\/agent\//, /^\/agrocycle\/templates\/\d+$/, /^\/agrocycle\/work-defs\//];

/**
 * Перевести системные тексты в РАЗОБРАННОМ ответе GET на месте (объект свежий,
 * общим он не бывает). Язык-источник и исключённые пути не трогаются, поэтому
 * на русском стоимость нулевая. Это единственное место, где клиент применяет
 * `srv` к ответам сервера: экраны получают уже показываемые строки и не знают,
 * какие из них системные.
 */
export function localizeServerPayload(path: string, payload: unknown): void {
  if (isSourceLocale() || payload === null || typeof payload !== 'object') return;
  const bare = path.split('?')[0]!;
  if (NOT_LOCALIZED.some((re) => re.test(bare))) return;
  walk(payload);
}

function walk(node: unknown): void {
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i += 1) {
      const item = node[i];
      if (typeof item === 'string') node[i] = localizeString(item);
      else if (item !== null && typeof item === 'object') walk(item);
    }
  } else if (node !== null && typeof node === 'object') {
    const record = node as Record<string, unknown>;
    for (const key of Object.keys(record)) {
      const item = record[key];
      if (typeof item === 'string') record[key] = localizeString(item);
      else if (item !== null && typeof item === 'object') walk(item);
    }
  }
}

/** Кириллица в строке есть, длина разумная — иначе перевода искать негде. */
const HAS_CYRILLIC = /[А-Яа-яЁё]/;

function localizeString(value: string): string {
  if (value.length < 2 || value.length > 1200 || !HAS_CYRILLIC.test(value)) return value;
  return cached(value) ?? value;
}

/** Только для тестов: словарь строится лениво один раз. */
export function resetServerTextForTests(): void {
  exact = null;
  longTexts = null;
  patterns = [];
  memo.clear();
  memoLocale = '';
}
